import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CELL_SIZE = 80;
const LABEL_WIDTH = 100;
const LABEL_HEIGHT = 40;
const MARGIN = { top: 20, right: 20, bottom: 20, left: 20 };
const BAR_HEIGHT = 20;
const BAR_GAP = 24;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  scrollContainer: {
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '240px',
  },
  interpretation: {
    marginTop: '16px',
    padding: '12px 16px',
    background: '#f9fafb',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
  },
  interpretationTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '6px',
  },
  interpretationText: {
    fontSize: '12px',
    color: '#6b7280',
    lineHeight: '1.5',
    margin: 0,
  },
  legend: {
    marginTop: '12px',
    display: 'flex',
    gap: '12px',
    fontSize: '11px',
    color: '#9ca3af',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  legendSwatch: {
    display: 'inline-block',
    width: '12px',
    height: '12px',
    borderRadius: '2px',
    verticalAlign: 'middle',
    marginRight: '4px',
  },
};

const FIELD_LABELS = {
  pest365: 'Pest365',
  pest365Plus: 'Pest365+',
  oneTime: 'One-Time',
  initialServiceVisit: 'ISV',
  mosquitoTickControl: 'MTC',
};

function getFieldLabel(field) {
  return FIELD_LABELS[field] || field;
}

export default function MarkovTransitionMatrix({ data }) {
  const svgRef = useRef(null);
  const barRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.states || !data.matrix || data.states.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { states, matrix } = data;
    const n = states.length;

    const gridWidth = n * CELL_SIZE;
    const gridHeight = n * CELL_SIZE;
    const totalWidth = MARGIN.left + LABEL_WIDTH + gridWidth + MARGIN.right;
    const totalHeight = MARGIN.top + LABEL_HEIGHT + gridHeight + MARGIN.bottom;

    svg.attr('width', totalWidth).attr('height', totalHeight);

    const gridX = MARGIN.left + LABEL_WIDTH;
    const gridY = MARGIN.top + LABEL_HEIGHT;

    // Color scale: sequential blue from light (#eff6ff) to dark (#1e40af)
    const colorScale = d3.scaleSequential()
      .domain([0, 1])
      .interpolator(d3.interpolateBlues);

    const g = svg.append('g');

    // Column labels ("To: ...")
    states.forEach((state, ci) => {
      g.append('text')
        .attr('x', gridX + ci * CELL_SIZE + CELL_SIZE / 2)
        .attr('y', gridY - 10)
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('font-weight', '600')
        .style('fill', '#6b7280')
        .text(`To: ${state}`);
    });

    // Row labels ("From: ...")
    states.forEach((state, ri) => {
      g.append('text')
        .attr('x', gridX - 10)
        .attr('y', gridY + ri * CELL_SIZE + CELL_SIZE / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .style('font-size', '11px')
        .style('font-weight', '600')
        .style('fill', '#6b7280')
        .text(`From: ${state}`);
    });

    // Heatmap cells
    for (let ri = 0; ri < n; ri++) {
      for (let ci = 0; ci < n; ci++) {
        const prob = matrix[ri][ci];
        const count = data.counts ? data.counts[ri][ci] : null;
        const x = gridX + ci * CELL_SIZE;
        const y = gridY + ri * CELL_SIZE;

        // Cell background
        g.append('rect')
          .attr('x', x)
          .attr('y', y)
          .attr('width', CELL_SIZE - 2)
          .attr('height', CELL_SIZE - 2)
          .attr('fill', colorScale(prob))
          .attr('stroke', '#e5e7eb')
          .attr('stroke-width', 0.5)
          .attr('rx', 4)
          .style('cursor', 'pointer')
          .on('mousemove', (event) => {
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              fromState: states[ri],
              toState: states[ci],
              probability: prob,
              count: count,
            });
          })
          .on('mouseleave', () => {
            setTooltip(null);
          });

        // Probability text
        const textColor = prob > 0.5 ? '#ffffff' : '#1f2937';
        g.append('text')
          .attr('x', x + (CELL_SIZE - 2) / 2)
          .attr('y', y + (CELL_SIZE - 2) / 2)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .style('font-size', '14px')
          .style('font-weight', '700')
          .style('fill', textColor)
          .style('pointer-events', 'none')
          .text(prob.toFixed(2));
      }
    }

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Stationary distribution bar chart
  useEffect(() => {
    if (!data || !data.stationaryDistribution || !data.states || data.states.length === 0) return;

    const barSvg = d3.select(barRef.current);
    barSvg.selectAll('*').remove();

    const { states, stationaryDistribution } = data;
    const n = states.length;

    const barWidth = 300;
    const barChartHeight = n * (BAR_HEIGHT + 6) + BAR_GAP;
    const labelW = 80;
    const chartW = barWidth - labelW - 50;

    barSvg.attr('width', barWidth).attr('height', barChartHeight);

    const maxVal = d3.max(stationaryDistribution) || 1;
    const xScale = d3.scaleLinear().domain([0, Math.max(maxVal, 0.5)]).range([0, chartW]);

    const barColor = d3.scaleSequential()
      .domain([0, 1])
      .interpolator(d3.interpolateBlues);

    const g = barSvg.append('g');

    states.forEach((state, i) => {
      const val = stationaryDistribution[i];
      const y = i * (BAR_HEIGHT + 6) + 4;

      // Label
      g.append('text')
        .attr('x', labelW - 6)
        .attr('y', y + BAR_HEIGHT / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .style('font-size', '11px')
        .style('font-weight', '600')
        .style('fill', '#374151')
        .text(state);

      // Bar
      g.append('rect')
        .attr('x', labelW)
        .attr('y', y)
        .attr('width', xScale(val))
        .attr('height', BAR_HEIGHT)
        .attr('fill', barColor(val))
        .attr('rx', 3);

      // Value label
      g.append('text')
        .attr('x', labelW + xScale(val) + 6)
        .attr('y', y + BAR_HEIGHT / 2)
        .attr('text-anchor', 'start')
        .attr('dominant-baseline', 'middle')
        .style('font-size', '11px')
        .style('font-weight', '600')
        .style('fill', '#6b7280')
        .text(`${(val * 100).toFixed(1)}%`);
    });

    return () => {
      barSvg.selectAll('*').remove();
    };
  }, [data]);

  if (!data || !data.states || !data.matrix || data.states.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price State Transitions (Markov Chain)</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No transition data available
        </div>
      </div>
    );
  }

  const { states, matrix, stationaryDistribution, field } = data;

  // Build interpretation text
  const interpretations = [];
  for (let ri = 0; ri < states.length; ri++) {
    // Find the highest probability transition from this state
    let maxProb = 0;
    let maxIdx = 0;
    for (let ci = 0; ci < states.length; ci++) {
      if (matrix[ri][ci] > maxProb) {
        maxProb = matrix[ri][ci];
        maxIdx = ci;
      }
    }
    const pct = (maxProb * 100).toFixed(0);
    const fromLabel = states[ri].toLowerCase();
    const toLabel = states[maxIdx].toLowerCase();
    if (ri === maxIdx) {
      interpretations.push(
        `${pct}% of ${fromLabel} addresses remain ${toLabel} next period`
      );
    } else {
      interpretations.push(
        `${pct}% of ${fromLabel} addresses transition to ${toLabel} next period`
      );
    }
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price State Transitions (Markov Chain)</div>
      <div style={styles.subtitle}>
        Transition probabilities between price states{field ? ` for ${getFieldLabel(field)}` : ''} — darker cells indicate higher probability
      </div>

      <div style={styles.scrollContainer}>
        <svg ref={svgRef} />
      </div>

      {/* Stationary Distribution */}
      {stationaryDistribution && stationaryDistribution.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
            Stationary Distribution (Long-Run Equilibrium)
          </div>
          <svg ref={barRef} />
        </div>
      )}

      {/* Interpretation */}
      <div style={styles.interpretation}>
        <div style={styles.interpretationTitle}>Key Insights</div>
        {interpretations.map((text, i) => (
          <p key={i} style={styles.interpretationText}>{text}</p>
        ))}
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <span style={{ fontWeight: 600, color: '#6b7280' }}>Probability:</span>
        <span>
          <span style={{ ...styles.legendSwatch, background: d3.interpolateBlues(0.05) }} />
          Low
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: d3.interpolateBlues(0.3) }} />
          0.3
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: d3.interpolateBlues(0.5) }} />
          0.5
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: d3.interpolateBlues(0.8) }} />
          High
        </span>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.fromState} → {tooltip.toState}
          </div>
          <div style={{ color: '#374151', marginBottom: '2px' }}>
            Probability: <strong>{(tooltip.probability * 100).toFixed(1)}%</strong>
          </div>
          {tooltip.count != null && (
            <div style={{ color: '#6b7280' }}>
              Observed transitions: {tooltip.count}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
